import React, { useEffect, useState } from "react";
import { api } from "../lib/config";
import { toast } from "sonner";
import { Upload, X } from "lucide-react";

export const AdminPageHeader = ({ title, description, action }) => (
  <div className="border-b border-[#9A8640]/20 px-10 py-8 flex items-start justify-between gap-6 sticky top-0 bg-[#171717] z-10">
    <div>
      <h1 className="font-serif text-3xl text-white">{title}</h1>
      {description && <p className="text-sm text-[#DDD4B2]/70 mt-1">{description}</p>}
    </div>
    {action}
  </div>
);

export const ImageUpload = ({ value, onChange, label = "Image" }) => {
  const [uploading, setUploading] = useState(false);

  const handle = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/admin/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange(r.data.url);
      toast.success("Image uploadée");
    } catch (err) {
      toast.error("Erreur upload");
    }
    setUploading(false);
  };

  return (
    <div>
      <label className="block text-xs uppercase tracking-[0.18em] text-[#9A8640] mb-2">{label}</label>
      <div className="flex gap-3 items-start">
        {value && (
          <div className="relative w-24 h-24 overflow-hidden border border-[#9A8640]/30">
            <img src={value.startsWith("/api") ? `${api.defaults.baseURL.replace("/api", "")}${value}` : value} alt="" className="w-full h-full object-cover" />
            <button type="button" onClick={() => onChange("")} className="absolute top-0 right-0 bg-[#171717] p-1 text-red-400"><X size={12} /></button>
          </div>
        )}
        <div className="flex-1 space-y-2">
          <input
            type="text"
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
            placeholder="URL de l'image ou uploadez un fichier"
            className="w-full bg-[#0f0f0f] border border-[#9A8640]/30 text-[#DDD4B2] px-4 py-2.5 text-sm focus:border-[#9A8640] outline-none"
          />
          <label className="inline-flex items-center gap-2 px-4 py-2 border border-[#9A8640]/40 text-xs text-[#DDD4B2] cursor-pointer hover:bg-[#9A8640]/10">
            <Upload size={14} /> {uploading ? "Upload…" : "Uploader depuis l'ordinateur"}
            <input type="file" accept="image/*" className="hidden" onChange={handle} disabled={uploading} />
          </label>
        </div>
      </div>
    </div>
  );
};

export const MultiImageUpload = ({ value = [], onChange, label = "Images" }) => {
  const [uploading, setUploading] = useState(false);
  const arr = Array.isArray(value) ? value : [];

  const handle = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      const uploaded = [];
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        const r = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        uploaded.push(r.data.url);
      }
      onChange([...arr, ...uploaded]);
      toast.success(`${uploaded.length} image(s) uploadée(s)`);
    } catch {
      toast.error("Erreur upload");
    }
    setUploading(false);
    e.target.value = "";
  };

  const remove = (i) => onChange(arr.filter((_, idx) => idx !== i));
  const move = (i, dir) => {
    const next = [...arr];
    const j = i + dir;
    if (j < 0 || j >= next.length) return;
    [next[i], next[j]] = [next[j], next[i]];
    onChange(next);
  };

  return (
    <div>
      <label className="block text-xs uppercase tracking-[0.18em] text-[#9A8640] mb-2">{label}</label>
      {arr.length > 0 && (
        <div className="grid grid-cols-3 sm:grid-cols-4 gap-3 mb-3">
          {arr.map((src, i) => (
            <div key={i} className="relative aspect-square overflow-hidden border border-[#9A8640]/30 group">
              <img src={src.startsWith("/api") ? `${api.defaults.baseURL.replace("/api", "")}${src}` : src} alt="" className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-1">
                {i > 0 && <button type="button" onClick={() => move(i, -1)} className="text-xs bg-[#9A8640] text-[#171717] px-2 py-1">←</button>}
                {i < arr.length - 1 && <button type="button" onClick={() => move(i, 1)} className="text-xs bg-[#9A8640] text-[#171717] px-2 py-1">→</button>}
                <button type="button" onClick={() => remove(i)} className="text-xs bg-red-500 text-white px-2 py-1"><X size={10} /></button>
              </div>
            </div>
          ))}
        </div>
      )}
      <label className="inline-flex items-center gap-2 px-4 py-2 border border-[#9A8640]/40 text-xs text-[#DDD4B2] cursor-pointer hover:bg-[#9A8640]/10">
        <Upload size={14} /> {uploading ? "Upload en cours…" : "Ajouter des images (multi-sélection)"}
        <input type="file" accept="image/*" multiple className="hidden" onChange={handle} disabled={uploading} />
      </label>
    </div>
  );
};

export const Field = ({ label, children }) => (
  <div>
    <label className="block text-xs uppercase tracking-[0.18em] text-[#9A8640] mb-2">{label}</label>
    {children}
  </div>
);

export const TextInput = (props) => (
  <input {...props} className={`w-full bg-[#0f0f0f] border border-[#9A8640]/30 text-[#DDD4B2] px-4 py-2.5 text-sm focus:border-[#9A8640] outline-none ${props.className || ""}`} />
);

export const TextArea = (props) => (
  <textarea {...props} className={`w-full bg-[#0f0f0f] border border-[#9A8640]/30 text-[#DDD4B2] px-4 py-2.5 text-sm focus:border-[#9A8640] outline-none resize-none ${props.className || ""}`} />
);

export const Select = (props) => (
  <select {...props} className={`w-full bg-[#0f0f0f] border border-[#9A8640]/30 text-[#DDD4B2] px-4 py-2.5 text-sm focus:border-[#9A8640] outline-none ${props.className || ""}`}>{props.children}</select>
);

export const BtnPrimary = ({ children, ...p }) => (
  <button {...p} className={`btn-gold px-5 py-2.5 text-xs uppercase tracking-[0.18em] inline-flex items-center gap-2 ${p.className || ""}`}>{children}</button>
);
export const BtnGhost = ({ children, ...p }) => (
  <button {...p} className={`px-4 py-2 text-xs uppercase tracking-[0.18em] border border-[#9A8640]/40 hover:bg-[#9A8640]/10 ${p.className || ""}`}>{children}</button>
);
export const BtnDanger = ({ children, ...p }) => (
  <button {...p} className={`px-4 py-2 text-xs uppercase tracking-[0.18em] border border-red-500/40 text-red-300 hover:bg-red-500/20 ${p.className || ""}`}>{children}</button>
);
